import React, { FC, Fragment, useState, ReactElement } from 'react';
import PropTypes from 'prop-types';
import ActionDelete from '@material-ui/icons/Delete';
import { makeStyles, alpha } from '@material-ui/core/styles';
import inflection from 'inflection';
import {
  Identifier,
  useTranslate,
  useDeleteMany,
  useRefresh,
  useNotify,
  useUnselectAll,
  CRUD_DELETE_MANY,
  useRedirect
} from 'ra-core';
import { Confirm, Button, ButtonProps } from 'ra-ui-materialui/lib';
import useNotifyServerError from '../hooks/useNotifyServerError';

const useStyles = makeStyles(
  theme => ({
    deleteButton: {
      color: theme.palette.error.main,
      '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        '@media (hover: none)': {
          backgroundColor: 'transparent'
        }
      }
    }
  }),
  { name: 'RaBulkDeleteWithConfirmButton' }
);

const defaultIcon = <ActionDelete />;

const DeleteButtonWithRedirect: FC<BulkDeleteWithConfirmButtonProps> = props => {
  const {
    basePath,
    classes: classesOverride,
    confirmTitle = 'ra.message.bulk_delete_title',
    confirmContent = 'ra.message.bulk_delete_content',
    icon = defaultIcon,
    label,
    onClick,
    resource,
    selectedIds,
    redirectPath,
    ...rest
  } = props;
  const [isOpen, setOpen] = useState(false);
  const classes = useStyles(props);
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const unselectAll = useUnselectAll();
  const refresh = useRefresh();
  const translate = useTranslate();
  const redirectTo = useRedirect();
  const [deleteMany, { loading }] = useDeleteMany(resource, selectedIds, {
    action: CRUD_DELETE_MANY,
    onSuccess: () => {
      if (props.redirect && props.redirectPath) {
        redirectTo(props.redirect, props.redirectPath);
      } else {
        refresh();
      }
      notify('ra.notification.deleted', {
        type: 'info',
        messageArgs: { smart_count: selectedIds.length }
      });
      unselectAll(resource);
    },
    onFailure: error => {
      notifyServerError(error, 'ra.notification.http_error');
      setOpen(false);
    }
  });

  const handleClick = e => {
    setOpen(true);
    e.stopPropagation();
  };

  const handleDialogClose = () => {
    setOpen(false);
  };

  const handleDelete = e => {
    deleteMany();

    if (typeof onClick === 'function') {
      onClick(e);
    }
  };

  return (
    <Fragment>
      <Button
        onClick={handleClick}
        label={label}
        className={classes.deleteButton}
        data-testid="delete-button"
        {...sanitizeRestProps(rest)}
      >
        {icon}
      </Button>
      <Confirm
        isOpen={isOpen}
        loading={loading}
        title={confirmTitle}
        content={confirmContent}
        translateOptions={{
          smart_count: selectedIds.length,
          name: translate(`resources.${resource}.forcedCaseName`, {
            smart_count: selectedIds.length,
            _: inflection.humanize(
              translate(`resources.${resource}.name`, {
                smart_count: selectedIds.length,
                _: inflection.inflect(resource, selectedIds.length)
              }),
              true
            )
          })
        }}
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </Fragment>
  );
};

const sanitizeRestProps = ({
  basePath,
  classes,
  filterValues,
  label,
  ...rest
}: Omit<
  BulkDeleteWithConfirmButtonProps,
  'resource' | 'selectedIds' | 'icon'
>) => rest;

export interface BulkDeleteWithConfirmButtonProps
  extends BulkActionProps,
    ButtonProps {
  confirmContent?: string;
  confirmTitle?: string;
  icon?: ReactElement;
  redirectPath?: string;
}

export interface BulkActionProps {
  basePath?: string;
  filterValues?: any;
  resource?: string;
  selectedIds?: Identifier[];
}

DeleteButtonWithRedirect.propTypes = {
  basePath: PropTypes.string,
  classes: PropTypes.object,
  confirmTitle: PropTypes.string,
  confirmContent: PropTypes.string,
  label: PropTypes.string,
  resource: PropTypes.string.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.any).isRequired,
  icon: PropTypes.element
};

DeleteButtonWithRedirect.defaultProps = {
  label: 'ra.action.delete'
};

export default DeleteButtonWithRedirect;
